import { useEffect, useMemo, useState } from 'react';
import { useLoaderData, useRevalidator } from 'react-router';

import { cn } from '@rocco/utils/cn';
import {
    Drawer,
    DrawerContent,
    DrawerDescription,
    DrawerTitle,
} from '@rocco/components/drawer';
import { CLEARCUT_FILL_COLOR } from '@rocco/components/image';
import { ItemShippingProvider } from '@modules/ecommerce/context/ItemShippingContext';
import { VariantShippingMethodsProvider } from '~/modules/ecommerce/context/VariantShippingMethodsContext';
import { useVariantSelectionState } from '@modules/product/context/VariantSelectionContext';
import SpecialtyPricingBanner from '@modules/product/component/specialty-pricing-banner/SpecialtyPricingBanner';
import TradeOnlyBanner from '@modules/product/component/trade-only-banner/TradeOnlyBanner';
import type { CartItem } from '~/modules/ecommerce/type/cart-item';
import { DrawerCartPanel } from './DrawerCartPanel';
import { DrawerShippingRows } from './DrawerShippingRows';
import ProductLocationsDrawer from '~/modules/product/component/product-details/ProductLocationsDrawer';
import { SpecifierLoginPrompt } from '~/modules/enquiry/component/specifier-login-prompt/SpecifierLoginPrompt';
import {
    useBookCta,
    useEnquireCta,
} from '~/modules/enquiry/hooks/use-page-ctas';
import type { loader } from '~/routes/_app.product.$urlSegment';
import { X } from 'lucide-react';
import { DrawerClose } from '@rocco/components/drawer';
import { WHITE_DISC } from '~/ui-rocco/pages/detail-shared';
import { TileSaveButton } from '~/modules/moodboards/components/TileActionPanel';
import { useFollowSelectorProxy } from '~/modules/moodboards/hooks';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import { DRAWER_CTA } from './drawer-cta';
import { GROUP_LABEL, MobileVariantSelector } from './MobileVariantSelector';
import {
    enquiryCtaLabel,
    InlineAddToCartButton,
    InlineSamplePurchaseButton,
    PurchasePriceBlock,
    SampleShippingInfo,
    useSizesInUse,
    useVariantEnquiryContext,
} from './purchase-flow-parts';

/** Cap on how many variant images to eagerly preload when the drawer opens. */
const VARIANT_IMAGE_PRELOAD_LIMIT = 16;

/** Fallback background for multiplied/clear-cut studio images (matches PhotoViewerModal). */
const MULTIPLIED_IMAGE_BG = CLEARCUT_FILL_COLOR;

export interface PurchaseDrawerProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    isBuyable: boolean;
    isSampleBuyable: boolean;
    /** Selected-variant price for the pinned footer — the pick changes the
     *  price, and the drawer covers the page's price block. Null hides it. */
    priceString: string | null;
    isPriceRange: boolean;
    isOnSale: boolean;
    retailPriceString: string | null;
    pricePrefix: string | null;
    /** Enquiry action label / modal title (e.g. "Enquire with Nero Tapware"). */
    enquiryLabel: string;
    /** Whether the enquiry CTA may show (the professional's audience gating). */
    showCTA: boolean;
    showSpecifierLoginPrompt: boolean;
    /**
     * `desktop` (the default) is the floating panel off the page's right
     * edge, opening on the variant's picture with Close and Save over it.
     * `mobile` is the same drawer as a bottom sheet: no picture — the page's
     * gallery is a swipe away and the ••• sheet carries Save — and the close
     * in the title's row. Everything under the title is the same.
     */
    presentation?: 'desktop' | 'mobile';
}

/**
 * The purchase drawer: the selected variant's image (the page gallery sits
 * behind the drawer), title, the list variant selector with its dynamic
 * option enabling, stock/shipping for the selection, then a pinned footer
 * led by Add to Cart where the product can be bought and by "Enquire about
 * this configuration" where it cannot, then a configured Book a meeting
 * button. Where-to-buy and the specifier login prompt keep their entry points
 * here.
 *
 * On desktop a floating panel off the page's right edge; on the phone the
 * same drawer as a bottom sheet without the picture — see `presentation`.
 *
 * Requires `VariantSelectionProvider` + `VariantOptionProvider` above it.
 */
export const PurchaseDrawer = ({
    open,
    onOpenChange,
    isBuyable,
    isSampleBuyable,
    priceString,
    isPriceRange,
    isOnSale,
    retailPriceString,
    pricePrefix,
    enquiryLabel,
    showCTA,
    showSpecifierLoginPrompt,
    presentation = 'desktop',
}: PurchaseDrawerProps) => {
    const mobile = presentation === 'mobile';
    const { product, shoppingCartSummary, whereToBuyEnabled } =
        useLoaderData<typeof loader>();
    const { selectedVariant, quantity } = useVariantSelectionState();
    // "Enquire about this variant" is the drawer's own copy; a configured
    // form's label replaces it.
    const enquire = useEnquireCta({
        source: 'ProductHeaderCTA',
        fixedLabel: enquiryCtaLabel(product.Variants.length),
    });
    const bookMeeting = useBookCta({ source: 'ProductHeaderCTA' });
    const variantEnquiry = useVariantEnquiryContext();
    const [whereToBuyOpen, setWhereToBuyOpen] = useState(false);
    const [cartOpen, setCartOpen] = useState(false);
    // What the add just created. The pane leads with this: the page's cart
    // summary is loader data, so it only catches up on the revalidation below.
    const [justAdded, setJustAdded] = useState<CartItem | null>(null);
    // The add posts to a resource route, which leaves this page's own cart
    // summary as it was — the pane reads that summary, so ask for it again.
    const revalidator = useRevalidator();

    // Every open starts on the product. The drawer stays mounted when it
    // closes, so an add left the track parked on the cart pane and the next
    // open came up on the cart, still leading with the item added last time.
    // Reset on the way in rather than on the way out: clearing as it closes
    // would slide the track back while the drawer is still animating away.
    useEffect(() => {
        if (!open) return;
        setCartOpen(false);
        setJustAdded(null);
    }, [open]);

    // The tile's own Save, shown rather than waited for: the drawer's image is
    // one picture with room for the control, where a tile has a grid of them.
    const { isInDesignBoard } = useHandleFollow();
    const { followSelector, openFollowSelector } = useFollowSelectorProxy({
        pinId: product.ID,
        pinType: 'ProductPin',
    });
    const isSaved = isInDesignBoard(product.ID, 'ProductPin');

    /**
     * The price rides in the Add to Cart button rather than sitting above it.
     *
     * Only where there is one figure to state and the button is the thing that
     * charges it: a range has no single price to name, and a sale price needs
     * the one it was struck from beside it, which a button label cannot carry.
     * Those keep the block above.
     */
    const priceInButton =
        isBuyable && !!priceString && !isPriceRange && !isOnSale;

    const sizesInUse = useSizesInUse();
    // What the reader needs before they can decide: whether it is in stock,
    // what shipping costs and when it lands. A product being sampled rather
    // than bought has its own version of the same facts.
    const stockAndDelivery = isBuyable ? (
        <DrawerShippingRows refundPolicyData={product} />
    ) : isSampleBuyable ? (
        <SampleShippingInfo />
    ) : null;
    const isEnquiryLed = !isBuyable && !isSampleBuyable;

    // A single image tracking the selected variant (no carousel/peek). Matches
    // the variant's image against the gallery's LibraryImages (the same source
    // the gallery uses), falling back to the first product image.
    const variantImage =
        (selectedVariant?.Image &&
            product.LibraryImages.find(
                (image) => image.ID === selectedVariant.Image?.ID,
            )) ||
        product.LibraryImages[0];
    const imageSrc =
        variantImage?.LibraryImageFile?.DetailPageSquare.Desktop ?? '';
    // The sheet on the phone draws no picture.
    const showImage = !mobile && Boolean(imageSrc);

    // A6 — background colour for the drawer thumbnail. Multiplied/clear-cut
    // studio shots use a neutral background (ImageBackgroundColor or the clear-cut fill token)
    // instead of cropping to fill. Matches the PhotoViewerModal pattern.
    const isMultiplied = Boolean(variantImage?.IsMultiplied);
    const isClearCut = Boolean(variantImage?.IsClearCut);
    const useSoftBackground = isMultiplied || isClearCut;
    const imageBackgroundColor =
        variantImage?.LibraryImageFile?.ImageBackgroundColor ??
        MULTIPLIED_IMAGE_BG;

    // Preload variant images so switching colour swaps instantly instead of
    // waiting on a fresh network fetch. Capped so a high-colour product doesn't
    // fire dozens of fetches on open; colours past the cap fetch on selection.
    const variantImageSrcs = useMemo(() => {
        const variantImageIds = new Set(
            product.Variants.map((v) => v.Image?.ID).filter(
                (id): id is number => typeof id === 'number',
            ),
        );
        return product.LibraryImages.filter((image) =>
            variantImageIds.has(image.ID),
        )
            .map((image) => image.LibraryImageFile?.DetailPageSquare.Desktop)
            .filter((src): src is string => Boolean(src))
            .slice(0, VARIANT_IMAGE_PRELOAD_LIMIT);
    }, [product]);

    return (
        <>
            {/* handleOnly with no rendered handle (handles are bottom-only)
                disables drag entirely; overlay-click and the X still close it. */}
            <Drawer
                direction={mobile ? 'bottom' : 'right'}
                handleOnly={!mobile}
                open={open}
                onOpenChange={onOpenChange}
            >
                <DrawerContent
                    // No drag handle on a right-side drawer (the handle slot
                    // otherwise mounts a stray pill at the top); scrollable makes
                    // DrawerContent's body wrapper a height-filling flex child so
                    // our inner column can scroll its content and pin the footer.
                    noHandle
                    scrollable={!mobile}
                    // 25.875rem (pxToRem(414)) so the drawer scales with the
                    // app's responsive root font-size, matching the rest of the
                    // page. Inline style (not a Tailwind class) because the base
                    // variant's max-w-sm sits under the same data+sm prefix and
                    // would win on CSS source order. The phone's sheet takes
                    // the screen's width.
                    style={
                        mobile
                            ? undefined
                            : { width: '25.875rem', maxWidth: '25.875rem' }
                    }
                    // Its own close, over the image with Save — see the bar below.
                    hideDefaultCloseButton
                    className={cn(
                        !mobile && [
                            // vaul 1.x ships no side-drawer transition — add the slide
                            // in/out ourselves (the established rocco sheet pattern).
                            'data-[state=open]:animate-in data-[state=open]:duration-300 data-[state=open]:slide-in-from-right',
                            'data-[state=closed]:animate-out data-[state=closed]:duration-200 data-[state=closed]:slide-out-to-right',
                            // A floating panel rather than a full-height sheet: held
                            // off three edges and rounded, so the page reads as the
                            // surface it is sitting on. The direction prefix matches
                            // the base rule it overrides, which is written the same
                            // way and would otherwise win.
                            'overflow-hidden rounded-[1.5rem]',
                            'data-[vaul-drawer-direction=right]:inset-y-6 data-[vaul-drawer-direction=right]:right-6',
                        ],
                    )}
                >
                    {/* Radix dialog a11y: a description silences the missing
                        aria-describedby warning; the visible title is below. */}
                    <DrawerDescription className="hidden" aria-hidden />

                    <VariantShippingMethodsProvider
                        selectedVariant={selectedVariant}
                        quantity={quantity}
                        estimatedSampleWeight={
                            product.Professional.SampleEstimatedWeight
                        }
                    >
                        <ItemShippingProvider>
                            {/* Above the track, so the corner is closed
                                whichever pane is showing — but only where that
                                pane does not close its own. The product pane
                                carries a close on its photograph, beside Save,
                                and drawing this one as well put two discs in
                                the same corner. */}
                            {cartOpen || !showImage ? (
                                <DrawerClose
                                    aria-label="Close"
                                    className={cn(
                                        'absolute z-20',
                                        // On the phone a plain X in the
                                        // title's row: the sheet is white,
                                        // where the disc would vanish.
                                        mobile
                                            ? 'top-4 right-3 inline-flex size-10 cursor-pointer items-center justify-center rounded-full text-[#181818]'
                                            : cn(WHITE_DISC, 'top-3 left-3'),
                                    )}
                                >
                                    <X className="size-[1.25rem]" />
                                </DrawerClose>
                            ) : null}
                            {/* Two panes on one track: the product, and the
                                cart it was added to. Adding slides the track
                                rather than opening the header's little menu at
                                the top of the window, several hundred pixels
                                from the button that filled it. */}
                            <div className="flex h-full min-h-0 w-full">
                                <div
                                    className={cn(
                                        'flex min-h-0 w-full shrink-0 flex-col transition-transform duration-300 ease-out motion-reduce:transition-none',
                                        // The sheet's height cap lives on the
                                        // pane: the column inside scrolls, the
                                        // footer stays pinned. 4.75rem is the
                                        // mobile header's height. The bottom
                                        // sheet has no height of its own, so
                                        // h-full would collapse each pane to
                                        // its content; left to stretch, the
                                        // shorter cart pane takes the product
                                        // pane's height and its footer sits
                                        // where the product's did.
                                        mobile
                                            ? 'max-h-[calc(100dvh-4.75rem)]'
                                            : 'h-full',
                                        cartOpen && '-translate-x-full',
                                    )}
                                    // Nothing behind the visible pane takes focus
                                    // or reads out while it is off to the side.
                                    inert={cartOpen ? true : undefined}
                                >
                                    {/* Scrollable content — grows to fill, scrolls when
                                it exceeds the viewport height. */}
                                    <div className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto overscroll-contain pb-4">
                                        {showImage ? (
                                            // A6 — multiplied/clear-cut images sit on a
                                            // neutral background (contain + multiply
                                            // blend) to avoid cropping studio shots.
                                            // Plain photos use object-cover as before.
                                            <div
                                                className={cn(
                                                    // Held 8px inside the panel and
                                                    // rounded with it, so the
                                                    // picture reads as a frame on
                                                    // the drawer rather than a
                                                    // photograph cut off by it.
                                                    'relative mx-2 mt-2 aspect-square shrink-0 overflow-hidden rounded-[1rem]',
                                                    useSoftBackground
                                                        ? 'flex items-center justify-center'
                                                        : '',
                                                )}
                                                style={
                                                    useSoftBackground
                                                        ? {
                                                              backgroundColor:
                                                                  imageBackgroundColor,
                                                          }
                                                        : {
                                                              backgroundColor:
                                                                  '#f3f4f6',
                                                          }
                                                }
                                            >
                                                <img
                                                    src={imageSrc}
                                                    alt={
                                                        variantEnquiry.messageSubject
                                                    }
                                                    className={cn(
                                                        'size-full',
                                                        useSoftBackground
                                                            ? 'object-contain mix-blend-multiply'
                                                            : 'object-cover',
                                                    )}
                                                />
                                                {/* Close and Save sit on the
                                                photograph, in the image
                                                viewer's white disc: the two
                                                surfaces cover the page the same
                                                way, so they answer to the same
                                                control. */}
                                                <div className="absolute inset-x-0 top-0 flex items-center justify-between p-3">
                                                    <DrawerClose
                                                        aria-label="Close"
                                                        className={WHITE_DISC}
                                                    >
                                                        <X className="size-[1.25rem]" />
                                                    </DrawerClose>
                                                    <TileSaveButton
                                                        saved={isSaved}
                                                        onSave={
                                                            openFollowSelector
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        ) : null}

                                        {/* Warm the cache for every variant image so
                                    colour switches are instant. */}
                                        <div
                                            className="hidden"
                                            aria-hidden="true"
                                        >
                                            {variantImageSrcs.map((src) => (
                                                <img
                                                    key={src}
                                                    src={src}
                                                    alt=""
                                                    loading="eager"
                                                />
                                            ))}
                                        </div>

                                        <div className="px-4 pt-5">
                                            {/* Under the picture: the image is
                                            what the reader recognises the
                                            product by, and the name confirms
                                            it. It is also the dialog's
                                            accessible name. */}
                                            <DrawerTitle
                                                className={cn(
                                                    'text-left font-sans text-xl leading-[1.2] font-medium tracking-[-0.02em] text-[#181818]',
                                                    mobile && 'pr-10',
                                                )}
                                            >
                                                {product.Title}
                                            </DrawerTitle>

                                            <div className="mt-5 [&:empty]:hidden">
                                                <SpecialtyPricingBanner
                                                    product={product}
                                                />
                                                <TradeOnlyBanner
                                                    selectedVariant={
                                                        selectedVariant
                                                    }
                                                />
                                            </div>

                                            <div className="mt-5">
                                                <MobileVariantSelector
                                                    variants={product.Variants}
                                                    sizes={sizesInUse}
                                                    // The page's own list, not the
                                                    // mobile frame's chips: this
                                                    // drawer sits beside the page
                                                    // that lists them.
                                                    layout="list"
                                                />
                                            </div>

                                            {/* Stock, shipping cost, delivery time
                                            and the refund policy. They close
                                            the scrolling column rather than
                                            ride in the pinned footer: the
                                            footer holds what the reader acts
                                            on — the price and the two buttons —
                                            and these are the detail behind it,
                                            which grows with the shipping
                                            methods a variant carries. */}
                                            {stockAndDelivery ? (
                                                <div className="mt-8 border-t border-black/10 pt-5">
                                                    <p className={GROUP_LABEL}>
                                                        Shipping
                                                    </p>
                                                    <div className="mt-3">
                                                        {stockAndDelivery}
                                                    </div>
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>

                                    {/* Pinned footer: the selection's live price
                                    (the pick changes it, and the drawer covers
                                    the page's price block) and the actions.

                                    Add to Cart leads wherever the product can
                                    be bought, dark and above the enquiry, as
                                    Shop Now does in the page's own cluster: a
                                    transaction is a different order of action
                                    from an enquiry, and the reader who opened
                                    this drawer has already picked a variant.
                                    The enquiry leads only where there is
                                    nothing to buy.

                                    No quantity here — it is changed in the
                                    cart, and it was taking the width of the
                                    button beside it. */}
                                    <div className="flex shrink-0 flex-col gap-3 border-t border-black/10 px-4 pt-4 pb-6">
                                        {priceInButton ? null : (
                                            <PurchasePriceBlock
                                                align="start"
                                                compact
                                                priceString={priceString}
                                                isPriceRange={isPriceRange}
                                                isInServiceableArea={
                                                    product.IsInServiceableArea
                                                }
                                                isOnSale={isOnSale}
                                                retailPriceString={
                                                    retailPriceString
                                                }
                                                pricePrefix={pricePrefix}
                                            />
                                        )}
                                        {isBuyable ? (
                                            <InlineAddToCartButton
                                                // The drawer shows the cart itself,
                                                // so the header menu stays shut.
                                                openCartPopup={false}
                                                pill={DRAWER_CTA(
                                                    'filled-light',
                                                )}
                                                label={
                                                    priceInButton
                                                        ? `${priceString} — Add to Cart`
                                                        : undefined
                                                }
                                                onAdded={(item) => {
                                                    setJustAdded(item);
                                                    void revalidator.revalidate();
                                                    setCartOpen(true);
                                                }}
                                            />
                                        ) : null}
                                        {showCTA ? (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    onOpenChange(false);
                                                    enquire.open({
                                                        title: enquiryLabel,
                                                        ctaLabel: enquiryLabel,
                                                        messageSubject:
                                                            variantEnquiry.messageSubject,
                                                        itemImage:
                                                            variantEnquiry.itemImage,
                                                        brandImage:
                                                            variantEnquiry.brandImage,
                                                    });
                                                }}
                                                className={DRAWER_CTA(
                                                    isBuyable
                                                        ? 'white-lifted'
                                                        : 'filled-light',
                                                )}
                                            >
                                                {enquire.label}
                                            </button>
                                        ) : null}
                                        {bookMeeting.inline ? (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    onOpenChange(false);
                                                    bookMeeting.open();
                                                }}
                                                className={DRAWER_CTA(
                                                    'white-lifted',
                                                )}
                                            >
                                                {bookMeeting.label}
                                            </button>
                                        ) : null}
                                        {isSampleBuyable ? (
                                            <InlineSamplePurchaseButton
                                                pill={DRAWER_CTA(
                                                    showCTA
                                                        ? 'white-lifted'
                                                        : 'filled-light',
                                                )}
                                                onAdded={() =>
                                                    onOpenChange(false)
                                                }
                                            />
                                        ) : null}
                                        {whereToBuyEnabled ? (
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setWhereToBuyOpen(true)
                                                }
                                                className={DRAWER_CTA(
                                                    'white-lifted',
                                                )}
                                            >
                                                Where to Buy
                                            </button>
                                        ) : null}
                                        {isEnquiryLed &&
                                        !showCTA &&
                                        showSpecifierLoginPrompt ? (
                                            <SpecifierLoginPrompt
                                                professionalName={
                                                    product.Professional.Title
                                                }
                                                showBottomBorder={false}
                                            />
                                        ) : null}
                                    </div>
                                </div>
                                <div
                                    className={cn(
                                        'flex min-h-0 w-full shrink-0 flex-col transition-transform duration-300 ease-out motion-reduce:transition-none',
                                        mobile
                                            ? 'max-h-[calc(100dvh-4.75rem)]'
                                            : 'h-full',
                                        cartOpen && '-translate-x-full',
                                    )}
                                    inert={cartOpen ? undefined : true}
                                >
                                    <DrawerCartPanel
                                        title="Added to cart"
                                        // The product page carries its own
                                        // cart summary, which the add
                                        // revalidates.
                                        items={
                                            shoppingCartSummary?.LastAddedItems ??
                                            []
                                        }
                                        subtotal={
                                            shoppingCartSummary?.ItemTotal
                                                ?.ShortFormat
                                        }
                                        justAdded={justAdded}
                                        onClose={() => onOpenChange(false)}
                                    />
                                </div>
                            </div>
                        </ItemShippingProvider>
                    </VariantShippingMethodsProvider>
                </DrawerContent>
            </Drawer>

            {/* The board picker, outside the drawer: it positions itself
                against the Save button and must not be clipped by the panel's
                rounded overflow. */}
            {followSelector}

            {whereToBuyEnabled ? (
                <ProductLocationsDrawer
                    product={product}
                    open={whereToBuyOpen}
                    toggleModal={setWhereToBuyOpen}
                />
            ) : null}
        </>
    );
};
